import { ROUTES } from "@/constants/routeConstants";
import { useAppNavigate } from "@/hooks/useAppNavigate";
import { useBreadcrumbs } from "@/hooks/useBreadcrumbs";
import { AxDataTableRequestInput, SpecialEntity, Template } from "@/lib/types";
import {
  deleteTemplateById,
  getTemplates,
  getStockTemplates,
  deleteStockTemplateById,
  addTemplate,
} from "@/sideEffects/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { selectActiveFirm } from "@/store/appSelectors";
import { useDispatch, useSelector } from "react-redux";
import { LoadingOverlay } from "@/components/LoadingOverlay";
import { AddRowButton } from "@/components/ax/dataTable/AddRowButton";
import { convertToTemplate } from "./utils/templateUtils";
import { TemplatePreview } from "./shared/TemplatePreview";
import { showApiError, showSuccess } from "@/lib/toast";
import { AxMediaViewer } from "@/components/ax/mediaGallery/AxMediaViewer";
import { setCurrentTemplate } from "@/store/templateEditor/templateEditorSlice";
import { useEffect, useMemo, useState } from "react";
import { PermissionChecker2 } from "@/components/permission/PermissionChecker2";
import { Button } from "@/components/ui/button";
import { Loader2, Pencil, Search, Trash, X } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { LoadingSmall } from "@/components/LoadingSmall";

type TabType = "my-templates" | "template-gallery";

// Interface for grouped templates by category
interface GroupedTemplates {
  [category: string]: Template[];
}

export const TemplatesPage = () => {
  const { t } = useTranslation();
  const activeFirm = useSelector(selectActiveFirm);
  const dispatch = useDispatch();
  const { goToTemplateAdd } = useAppNavigate();
  const [activeTab, setActiveTab] = useState<TabType>("my-templates");

  useBreadcrumbs([{ path: ROUTES.TEMPLATES, name: t("entities.template") }]);

  useEffect(() => {
    dispatch(setCurrentTemplate(null));
  }, [dispatch]);

  const [input] = useState<AxDataTableRequestInput>({
    search: "",
    pageNumber: 1,
    numberOfObjectsPerPage: 9999,
  });

  const { data: stockTemplates } = useQuery({
    queryKey: ["getTemplates", activeTab],
    // @ts-ignore typing error for now, it's about firmGuid and can be ignored
    queryFn: () => {
      return getStockTemplates(input);
    },
    refetchOnMount: true,
  });

  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);

  const stockTemplatesData = stockTemplates?.data?.result?.map((template) => convertToTemplate(template)) || [];

  // Group templates by category
  const groupTemplatesByCategory = (templates: Template[]): GroupedTemplates => {
    return templates.reduce((acc: GroupedTemplates, template) => {
      const category = template.categoryName || t("template.uncategorized");
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(template);
      return acc;
    }, {});
  };

  // Render templates grid
  const renderTemplateGrid = (templates: Template[]) => (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
      {templates.map((template) => (
        <TemplateCard key={template.id} {...template} entityPerm="DsStockTemplate" />
      ))}
    </div>
  );

  // Render templates grouped by category
  const renderGroupedTemplateGrid = (templates: Template[]) => {
    const groupedTemplates = groupTemplatesByCategory(templates);
    const uncategorizedKey = t("template.uncategorized");

    return (
      <div className="space-y-8">
        {Object.entries(groupedTemplates)
          .sort(([categoryA], [categoryB]) => {
            // Always put "Uncategorized" at the end
            if (categoryA === uncategorizedKey) return 1;
            if (categoryB === uncategorizedKey) return -1;
            return categoryA.localeCompare(categoryB);
          })
          .map(([category, templates]) => (
            <div key={category} className="space-y-4">
              <h2 className="text-xl font-semibold border-b pb-2">{category}</h2>
              {renderTemplateGrid(templates)}
            </div>
          ))}
      </div>
    );
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSearching(true);
    // Implement search logic here if needed
    setIsSearching(false);
  };

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchQuery("");
  };

  const filteredStockTemplates = useMemo(
    () =>
      stockTemplatesData.filter(
        (template) =>
          template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          template.viewport_size.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (template.categoryName && template.categoryName.toLowerCase().includes(searchQuery.toLowerCase()))
      ),
    [stockTemplatesData, searchQuery]
  );

  return (
    <div className="container py-6">
      <Tabs defaultValue="my-templates" onValueChange={(value) => setActiveTab(value as TabType)}>
        <div className="flex justify-between items-center mb-6">
          <TabsList>
            <TabsTrigger value="my-templates">{t("template.myTemplates")}</TabsTrigger>
            <TabsTrigger value="template-gallery">{t("template.templateGallery")}</TabsTrigger>
          </TabsList>
          <PermissionChecker2 entity="DsTemplate" rights={["Add"]}>
            <AddRowButton
              title={t("common.addNewEntity", { entity: t("entities.template") })}
              onClickAdd={goToTemplateAdd}
            />
          </PermissionChecker2>
        </div>

        <TabsContent value="my-templates" className="mt-0">
          <AxMediaViewer
            uniqQueryKey={"getTemplates"}
            fetcher={getTemplates}
            dynamicRequestParams={{ FirmGuid: activeFirm?.firmGuid }}
            itemKey="id"
            renderItem={(item) => <TemplateCard {...(convertToTemplate(item) as Template)} entityPerm="DsTemplate" />}
            noResultsText={t("template.noTemplates")}
          />
        </TabsContent>

        <TabsContent value="template-gallery" className="mt-0">
          <div className="my-6">
            <form onSubmit={handleSearch} className="flex items-center space-x-2">
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
                <Input
                  type="text"
                  placeholder={t("template.searchPlaceholder")}
                  value={searchQuery}
                  onChange={handleSearchInputChange}
                  className="pl-9 pr-4"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={handleClearSearch}
                    className="absolute right-2.5 top-2.5"
                    aria-label={t("content.clear")}
                  >
                    <X className="h-4 w-4 text-gray-500" />
                  </button>
                )}
              </div>
              <Button type="submit" disabled={isSearching}>
                {isSearching ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Search className="h-4 w-4 mr-2" />}
                {t("common.search")}
              </Button>
            </form>
          </div>

          {renderGroupedTemplateGrid(filteredStockTemplates)}
        </TabsContent>
      </Tabs>
    </div>
  );
};

const TemplateCard: React.FC<Template & { entityPerm: SpecialEntity }> = ({
  id,
  name,
  viewport_size,
  canvas_data,
  categoryName,
  entityPerm,
}) => {
  const { t } = useTranslation();
  const activeFirm = useSelector(selectActiveFirm);
  const { goToTemplateEditor } = useAppNavigate();
  const queryClient = useQueryClient();

  const deleteTemplateMutation = useMutation({
    mutationFn: () => deleteTemplateById(String(activeFirm?.firmGuid), id),
    onSuccess: () => {
      showSuccess(t("common.deletedSuccess"));
      queryClient.invalidateQueries({ queryKey: ["getTemplates"] });
    },
    onError: showApiError,
  });

  const deleteStockTemplateMutation = useMutation({
    mutationFn: () => deleteStockTemplateById(String(activeFirm?.firmGuid), id),
    onSuccess: () => {
      showSuccess(t("common.deletedSuccess"));
      queryClient.invalidateQueries({ queryKey: ["getTemplates"] });
    },
    onError: showApiError,
  });

  const [using, setUsing] = useState(false);

  const duplicateContentToMyAccount = async () => {
    const response = await addTemplate({
      firmGuid: String(activeFirm?.firmGuid),
      name,
      viewport_size,
      canvas_data,
    });

    if (response.data.guid) {
      goToTemplateEditor(response.data.guid);
    }
  };

  const onClick = async () => {
    if (entityPerm === "DsTemplate") {
      goToTemplateEditor(id);
    } else {
      try {
        setUsing(true);
        await duplicateContentToMyAccount();
        setUsing(false);
      } catch (error) {
        setUsing(false);
        showApiError(error as any);
      }
    }
  };

  const onDelete = () => {
    if (entityPerm === "DsTemplate") {
      deleteTemplateMutation.mutate();
    } else {
      deleteStockTemplateMutation.mutate();
    }
  };

  return (
    <LoadingOverlay isLoading={deleteTemplateMutation.isPending || deleteStockTemplateMutation.isPending}>
      <div className="group relative overflow-hidden bg-card rounded-lg border shadow-sm transition-all duration-200 hover:shadow-md hover:scale-[1.02]">
        <div className="cursor-pointer p-4">
          <div className="relative mb-4 py-6 overflow-hidden rounded-md border bg-muted/50">
            <TemplatePreview data={canvas_data} previewSize="small" />
          </div>
          <h3 className="font-semibold text-lg truncate">{name}</h3>
          <p className="text-sm text-muted-foreground">
            {categoryName && `${categoryName} - `}
            {viewport_size}
          </p>
        </div>

        <PermissionChecker2 entity={entityPerm} rights={["GetDetails"]}>
          <div className="bottom-0 left-0 right-0 flex justify-between items-center p-3 bg-muted/10 border-t">
            <Button variant="outline" size="sm" className="h-8 px-2 text-primary" onClick={onClick} disabled={using}>
              {entityPerm === "DsTemplate" ? (
                <>
                  <Pencil className="h-4 w-4 mr-1" />
                  {t("common.edit")}
                </>
              ) : (
                <>{t("Use")}</>
              )}
              {using && <LoadingSmall />}
            </Button>
            <PermissionChecker2 entity={entityPerm} rights={["Delete"]}>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" size="sm" className="h-8 px-2 text-destructive">
                    <Trash className="h-4 w-4 mr-1" />
                    {t("common.deleteEntity.buttonText")}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>{t("common.deleteEntity.title")}</AlertDialogTitle>
                    <AlertDialogDescription>{t("common.deleteEntity.description")}</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                    <AlertDialogAction onClick={onDelete}>{t("common.deleteEntity.buttonText")}</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </PermissionChecker2>
          </div>
        </PermissionChecker2>
      </div>
    </LoadingOverlay>
  );
};
